import { Column, Row } from '@/components/SettingsLayout';
import { Input, SwitchInput, Text } from '@/ui';
import RadioInput from '@/ui/RadioInput/RadioInput';
import RadioGroupContainer from '@/components/RadioGroupContainer/RadioGroupContainer';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import { FeatureGroup, FeatureType } from '@/services/Features/features.service';
import { formatTestId } from '@/utils/helpers/formatTestId';

export enum Options {
  UNLIMITED = 'unlimited',
  LIMITED = 'limited',
}

export type Props = {
  featureGroup: FeatureGroup;
};

function FeatureGroupRestrictions({ featureGroup }: Props) {
  const { t } = useTranslation();
  const { control, register } = useFormContext();

  return (
    <Column gapSize='12' fullWidth>
      <Text size='l' weight='medium'>
        {t(`Restrictions.${featureGroup.name}`)}
      </Text>

      <Column gapSize='16' fullWidth>
        {featureGroup.features.map((feature) => {
          const isBoolean = feature.type === FeatureType.BOOLEAN;
          const Container = isBoolean ? Row : Column;

          return (
            <Container key={feature._id} gapSize='8' fullWidth justify='justify-between'>
              <Text size='m' weight='light-medium'>
                {t(`Features.${feature.name}`)}
              </Text>
              <Controller
                name={`${featureGroup.name}.${feature.name}`}
                control={control}
                rules={{ required: isBoolean ? false : true }}
                render={({ field }) => {
                  if (isBoolean) {
                    return (
                      <SwitchInput
                        field={field}
                        checked={field?.value?.value === '1'}
                        onInput={(value) => {
                          field.onChange({
                            ...feature,
                            value: value ? '1' : '0',
                          });
                        }}
                        testId={formatTestId(
                          'feature-group-restrictions-switch-input',
                          feature.name,
                        )}
                      />
                    );
                  }
                  return (
                    <RadioGroupContainer fullWidth>
                      {Object.values(Options).map((option) => {
                        if (option === Options.LIMITED) {
                          return (
                            <Row key={option} fullWidth justify='justify-between'>
                              <Column gapSize='4'>
                                <RadioInput
                                  id={option}
                                  checked={field?.value?.value === option}
                                  onChange={() =>
                                    field.onChange({
                                      ...feature,
                                      value: option,
                                    })
                                  }
                                  label={t(`Restrictions.${option.toLowerCase()}`)}
                                  testId={formatTestId(
                                    'feature-group-restrictions-radio-button',
                                    option,
                                  )}
                                />
                                <div className={styles.restrictionComment}>
                                  <Text size='xs' weight='light-medium' color='base-grey'>
                                    {t(`Restrictions.limited_${feature.name}_comment`)}
                                  </Text>
                                </div>
                              </Column>
                              <div />

                              {field?.value?.value === Options.LIMITED && (
                                <div className={styles.smallInput}>
                                  <Input
                                    variant='full'
                                    text='center'
                                    type='number'
                                    min='1'
                                    placeholder='1'
                                    {...register(`${featureGroup.name}.${feature.name}.limit`)}
                                    defaultValue='1'
                                  />
                                </div>
                              )}
                            </Row>
                          );
                        } else {
                          return (
                            <RadioInput
                              key={option}
                              id={option}
                              checked={field?.value?.value === option}
                              onChange={() =>
                                field.onChange({
                                  ...feature,
                                  value: option,
                                })
                              }
                              label={t(`Restrictions.${option.toLowerCase()}`)}
                              testId={formatTestId(
                                'feature-group-restrictions-radio-button',
                                option,
                              )}
                            />
                          );
                        }
                      })}
                    </RadioGroupContainer>
                  );
                }}
              />
            </Container>
          );
        })}
      </Column>
    </Column>
  );
}

export default FeatureGroupRestrictions;
